import React, { useMemo, useEffect, useState } from "react";
import {
  Table,
  Text,
  Flex,
  useColorModeValue,
  Box,
  Select,
  HStack,
  Tooltip,
  IconButton,
  Icon,
  TableContainer,
  useDisclosure,
  Img,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import Card from "components/Card/Card.js";
import Header from "components/ui/table/Header";
import Body from "components/ui/table/Body";
import Pagination from "components/ui/table/Pagination";
import { Search } from "components/ui/table/Search";
import { FaPlus } from "react-icons/fa6";
import useDataTable from "hooks/useDataTable";
import { useForm } from "react-hook-form";
import { useDeleteUser } from "queries/useUser";
import { useGetPaginatedUsers } from "queries/useUser";
import { dynamicSort } from "utils/helpers";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import { RiEdit2Fill } from "react-icons/ri";
import { FaTrash } from "react-icons/fa";
import { CreatedAt } from "components/CellElements";
import { Basic } from "components/CellElements";
import { UserName } from "components/CellElements";

function Users() {
  const { formMethods, table, deleteModal, navigate, bgButton } = useUsers();

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <HStack spacing={4} mb={4} mx="6">
          <Search methods={formMethods} />
          <Box>
            <Select
              w="160px"
              placeholder=""
              {...formMethods.register("userType")}
              defaultValue={localStorage.getItem("usersUserType") || ""}
            >
              <option value="">All User Types</option>
              <option value="Agent">Agent</option>
              <option value="Coach">Coach</option>
              <option value="Executive">Executive</option>
              <option value="Parent">Parent</option>
              <option value="Reporter">Reporter</option>
              <option value="Professional Player">Professional Player</option>
              <option value="College Player">College Player</option>
              <option value="Spouse">Spouse</option>
              <option value="College">College</option>
            </Select>
          </Box>
          <Tooltip label="Add New User" placement="top">
            <IconButton
              aria-label="add new user"
              icon={<Icon as={FaPlus} boxSize={4} />}
              colorScheme="primary"
              bg={bgButton}
              onClick={() => navigate("/users/create")}
            />
          </Tooltip>
        </HStack>
        <TableContainer>
          <Table {...table.getTableProps()}>
            <Header {...table.header} />
            <Body {...table.body} />
          </Table>
        </TableContainer>
        <Pagination mt="10" {...table.pagination} />
        <DeleteConfirmationModal {...deleteModal} />
      </Card>
    </Flex>
  );
}

export default Users;

const useUsers = () => {
  const navigate = useNavigate();

  const deleteModal = useDisclosure();
  const formMethods = useForm({
    defaultValues: {
      search: localStorage.getItem("usersSearch") || "",
      userType: localStorage.getItem("usersUserType") || "",
    },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    const savedLimit = localStorage.getItem("usersLimit");
    return savedLimit ? parseInt(savedLimit, 10) : 50;
  });
  const [currentSort, setCurrentSort] = useState([["createdAt", "DESC"]]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const watchSearch = formMethods?.watch("search");
  const watchUserType = formMethods?.watch("userType");

  const getUsers = useGetPaginatedUsers({
    page: currentPage,
    limit,
    search: watchSearch,
    order: currentSort,
    filters: {
      ...(watchUserType ? { userType: watchUserType } : {}),
    },
  });
  const deleteUser = useDeleteUser({
    onSuccess: () => deleteModal.onClose(),
  });

  const onSort = dynamicSort(["createdAt"]);
  const data = useMemo(() => getUsers.data?.data?.users || [], [
    JSON.stringify(getUsers.data),
  ]);

  const onDelete = (user) => {
    setSelectedUserId(user?.id);
    deleteModal.onOpen();
  };

  const onEdit = (user) => navigate(`/users/view/${user?.id}`);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        id: "name",
        accessor: (user) => [
          user.firstName,
          user.lastName,
          user.email,
          user.team?.logoUrl,
          user.team,
        ],
        Cell: ({ value: [firstName, lastName, email, logoUrl, team] }) => (
          <UserName
            name={`${firstName} ${lastName}`}
            email={email}
            logo={logoUrl}
            team={team}
          />
        ),
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "User Type",
        accessor: "userType",
        Cell: ({ value }) => <Basic text={value} />,
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) => <CreatedAt date={value} />,
      },
      {
        accessor: "id",
        disableSortBy: true,
        Cell: ({ row }) => (
          <HStack spacing={3}>
            <Tooltip label="View" placement="top">
              <IconButton
                aria-label="view user"
                icon={<Icon as={RiEdit2Fill} boxSize={5} />}
                onClick={() => onEdit(row.original)}
              />
            </Tooltip>
            <Tooltip label="Delete" placement="top">
              <IconButton
                aria-label="delete user"
                icon={<Icon as={FaTrash} boxSize={4} />}
                colorScheme="red"
                onClick={() => onDelete(row.original)}
              />
            </Tooltip>
          </HStack>
        ),
      },
    ],
    []
  );

  const sortTable = useMemo(() => {
    return currentSort.map(([column, direction]) => {
      let id = column?.replace("name", "firstName") || "createdAt";
      let desc = direction === "ASC" ? false : true;
      return { id, desc };
    });
  }, [currentSort]);

  const pageCount = useMemo(() => getUsers.data?.data?.totalPages || 0, [
    getUsers.data,
  ]);

  const table = useDataTable({
    desktopHiddenColumns: [],
    mobileHiddenColumns: [],
    data,
    columns,
    initialState: {
      pageCount,
      pageIndex: currentPage - 1,
      pageSize: limit,
      sortBy: sortTable,
    },
    setter: { setCurrentPage, setLimit },
    isLoading: getUsers.isLoading,
  });

  useEffect(() => {
    getUsers.refetch({
      page: table.pagination.pageIndex,
      limit: table.pagination.pageSize,
      order: currentSort,
      search: watchSearch,
    });

    localStorage.setItem("usersPage", table.pagination.pageIndex.toString());
    localStorage.setItem("usersLimit", table.pagination.pageSize.toString());

    if (watchSearch !== undefined) {
      localStorage.setItem("usersSearch", watchSearch || "");
    }

    if (watchUserType !== undefined) {
      localStorage.setItem("usersUserType", watchUserType || "");
    }
  }, [
    table.pagination.pageIndex,
    table.pagination.pageSize,
    watchSearch,
    watchUserType,
    JSON.stringify(table.sort?.sortBy),
  ]);

  useEffect(() => {
    let sort = table.sort?.sortBy?.map((s) => {
      let id = s.id?.replace("name", "firstName") || "createdAt";
      let desc = s.desc == false ? "ASC" : "DESC";
      return [id, desc];
    });

    setCurrentSort(sort);
  }, [JSON.stringify(table.sort?.sortBy)]);

  const bgButton = useColorModeValue(
    "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
    "gray.800"
  );

  return {
    bgButton,
    formMethods,
    table,
    navigate,
    deleteModal: {
      ...deleteModal,
      onConfirmDelete: () => deleteUser.mutate(selectedUserId),
      isLoading: deleteUser.isLoading,
      title: "Are you sure you want to delete this user?",
    },
  };
};
